// ─────────────────────────────────────────────
//  testSnapshot — WHAT a testing-mode snapshot contains, and what it admits it
//  does not.
//
//  Pure. The reading and writing live in features/testing/captureDay.js; this
//  is the plan and the manifest, so both the capture and every screen that
//  renders captured data agree about which collections were even attempted.
//
//  ⚠️ THE MANIFEST IS THE POINT. A collection that was never captured must
//  render as "not captured in this snapshot", NEVER as zero. An empty board
//  meaning "I did not load it" is indistinguishable from "that day was empty",
//  and this codebase has three separate incidents of exactly that confusion.
//  Without a manifest the difference is not expressible at all.
// ─────────────────────────────────────────────

// How each collection is scoped. `day` = facility + date, `facility` = the whole
// facility, `tenant` = everything, `roster` = only the accounts on that day's
// roster.
// ⚠️ THE DAY FIELD IS NOT `date` EVERYWHERE, AND GETTING IT WRONG IS SILENT.
// A day-scoped read filtered on a field a collection does not have returns
// ZERO rows and no error, so the manifest records `captured: true, count: 0` —
// the collection reports itself as a genuinely quiet day. That is exactly the
// confusion the manifest exists to prevent, arriving through the capture
// instead. Two were wrong on the first pass:
//   • `tasks` stores `dateKey`, not `date` (firestore.js listenToTasks).
//   • `alerts` has NO day field at all — listenToAlerts filters facility and
//     status only, so it must be captured facility-scoped.
// `dateField` names the real one; scope "facility" means there is none.
export const SNAPSHOT_PLAN = [
  { collection: "rooms",        scope: "facility" },
  { collection: "assignments",  scope: "day", dateField: "date" },
  { collection: "daily_boards", scope: "day", dateField: "date" },
  { collection: "or_cases",     scope: "day", dateField: "date" },
  { collection: "breaks",       scope: "day", dateField: "date" },
  // No day field on an alert document — facility-scoped, filtered in the app.
  { collection: "alerts",       scope: "facility" },
  { collection: "tasks",        scope: "day", dateField: "dateKey" },
  { collection: "staff",        scope: "tenant" },
  // ⚠️ NARROW ON PURPOSE. `users` is the AUTH record and carries push tokens —
  // it is not the people directory (`staff` is). Capture only the accounts on
  // the chosen day's roster, never the tenant.
  { collection: "users",        scope: "roster" },
];

export const SNAPSHOT_COLLECTIONS = SNAPSHOT_PLAN.map((p) => p.collection);

/**
 * A manifest entry per collection: whether it was captured, how many documents,
 * and — when it failed — why. A failure is recorded, never swallowed: a
 * collection the snapshot could not read is exactly the one a tester must not
 * mistake for empty.
 */
export function buildManifest(results = []) {
  const byCollection = {};
  for (const r of results) {
    if (!r || !r.collection) continue;
    byCollection[r.collection] = {
      captured: !!r.captured,
      count: Number.isFinite(r.count) ? r.count : 0,
      error: r.error ? String(r.error) : null,
    };
  }
  return {
    version: 1,
    capturedAt: new Date().toISOString(),
    byCollection,
  };
}

// Did this snapshot even try, and succeed, for that collection?
//
// ⚠️ UNKNOWN IS NOT CAPTURED. A manifest with no entry for a collection means
// nobody read it, so the answer is false — never "assume it worked".
export function isCaptured(manifest, collection) {
  return !!manifest?.byCollection?.[collection]?.captured;
}

export function capturedCount(manifest, collection) {
  return manifest?.byCollection?.[collection]?.count ?? 0;
}

// The collections a tester should be warned about: attempted and failed, or
// never attempted at all.
export function missingCollections(manifest, plan = SNAPSHOT_COLLECTIONS) {
  return plan.filter((c) => !isCaptured(manifest, c));
}

/**
 * One sentence for a screen whose data was not captured. Returns "" when the
 * collection IS captured, so a caller can render it unconditionally and get
 * nothing when there is nothing to say.
 */
export function notCapturedNote(manifest, collection) {
  if (isCaptured(manifest, collection)) return "";
  const err = manifest?.byCollection?.[collection]?.error;
  return err
    ? `Not captured in this snapshot (${err}). This is not an empty day.`
    : "Not captured in this snapshot. This is not an empty day.";
}

// A snapshot with no rooms and no assignments cannot render a board, so entering
// it would show a convincing empty hospital. Refuse before the reload, not after.
export function isSnapshotUsable(manifest) {
  return isCaptured(manifest, "rooms") && isCaptured(manifest, "assignments");
}
